// src/pages/Unsubscribe.js

import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { CheckCircleIcon, ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/solid';
import UrlConfig from '../../data/UrlConfig'; // Asegúrate de que la ruta sea correcta
import { postApi } from '../../services/apiService'; // <-- 1. IMPORTAMOS postApi

const API_URLS = UrlConfig;

// Hook para obtener parámetros de la URL
function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const Unsubscribe = () => {
  const query = useQuery();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState(query.get('code') || 'general');
  const [reasons, setReasons] = useState([]);
  const [expectations, setExpectations] = useState('');
  const [improvements, setImprovements] = useState('');
  const [otherReason, setOtherReason] = useState('');
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    // Obtener el email desde el parámetro 'email' de la URL
    const userEmail = query.get('email');
    if (userEmail) {
      setEmail(userEmail);
    } else {
      setError("No email address was provided. Please check the link you followed.");
    }
  }, [query]);

  const handleReasonChange = (e) => {
    const { value, checked } = e.target;
    if (checked) {
      setReasons([...reasons, value]);
    } else {
      setReasons(reasons.filter((reason) => reason !== value));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      setError("Email is missing. Cannot process unsubscription.");
      return;
    }
    setIsLoading(true);
    setError('');

    const feedbackData = {
      email,
      code: code,
      reasons,
      otherReason: reasons.includes('Other') ? otherReason : '',
      expectations,
      improvements,
    };

    try {
      // --- 2. USAMOS postApi EN LUGAR DE FETCH ---
      // Tu apiService ya maneja la conversión a JSON y la verificación de errores.
      // Si la promesa se resuelve, la operación fue exitosa.
      await postApi(UrlConfig.subscriptions_cancel, feedbackData);

      setSubmitted(true);
      // --- 3. MANEJO DE ERRORES SIMPLIFICADO ---
      // El bloque `catch` ahora recibirá el error lanzado por postApi
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  const unsubscribeReasons = [
    { id: 'reason-irrelevant', label: "The content isn't relevant to me." },
    { id: 'reason-too-many', label: 'I receive too many emails.' },
    { id: 'reason-no-signup', label: "I don't remember signing up." },
    { id: 'reason-temporary', label: 'This is just temporary, I might be back!' },
    { id: 'reason-other', label: 'Other' },
  ];

  if (submitted) {
    return (
      <div className="bg-gray-900 text-white min-h-screen flex items-center justify-center font-sans p-4">
        <div className="w-full max-w-2xl text-center bg-gray-800 border border-green-700/50 rounded-xl p-8 shadow-2xl">
          <CheckCircleIcon className="w-16 h-16 mx-auto mb-4 text-green-500" />
          <h1 className="text-3xl font-bold mb-2">You've Been Unsubscribed</h1>
          <p className="text-gray-400 mb-6">
            You have been successfully removed from our mailing list. Thank you for your feedback!
          </p>
          <Link to="/" className="text-sky-400 hover:text-sky-300 transition-colors">
            Return to the homepage
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 text-white min-h-screen flex items-center justify-center font-sans p-4">
      <div className="w-full max-w-2xl">
        <div className="bg-gray-800 border border-gray-700/50 rounded-xl p-8 shadow-2xl">
          <div className="text-center mb-8">
            <ExclamationTriangleIcon className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
            <h1 className="text-3xl font-bold">We're Sorry to See You Go</h1>
            <p className="text-gray-400 mt-2">
              To complete your unsubscription from <span className="font-semibold text-gray-300">{email}</span>, please let us know why you're leaving. Your feedback is extremely valuable to us.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* --- SECCIÓN 1: RAZONES DE LA CANCELACIÓN --- */}
            <div>
              <label className="block text-lg font-semibold mb-3">1. Why are you leaving? (Optional)</label>
              <div className="space-y-3">
                {unsubscribeReasons.map((reason) => (
                  <div key={reason.id} className="flex items-center">
                    <input
                      id={reason.id}
                      type="checkbox"
                      value={reason.label}
                      onChange={handleReasonChange}
                      className="h-5 w-5 rounded border-gray-600 bg-gray-700 text-sky-500 focus:ring-sky-600"
                    />
                    <label htmlFor={reason.id} className="ml-3 text-gray-300">
                      {reason.label}
                    </label>
                  </div>
                ))}
                {reasons.includes('Other') && (
                  <input
                    type="text"
                    value={otherReason}
                    onChange={(e) => setOtherReason(e.target.value)}
                    placeholder="Please specify"
                    className="mt-2 w-full p-2 rounded-md bg-gray-900/70 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-sky-400"
                  />
                )}
              </div>
            </div>

            {/* --- SECCIÓN 2: QUÉ ESPERABAS --- */}
            <div>
              <label htmlFor="expectations" className="block text-lg font-semibold mb-2">
                2. What were you hoping to find here? (Optional)
              </label>
              <textarea
                id="expectations"
                value={expectations}
                onChange={(e) => setExpectations(e.target.value)}
                rows="3"
                className="w-full p-3 rounded-md bg-gray-900/70 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-sky-400"
                placeholder="e.g., I was looking for content about..."
              />
            </div>

            {/* --- SECCIÓN 3: CÓMO PODEMOS MEJORAR --- */}
            <div>
              <label htmlFor="improvements" className="block text-lg font-semibold mb-2">
                3. How could we improve? (Optional)
              </label>
              <textarea
                id="improvements"
                value={improvements}
                onChange={(e) => setImprovements(e.target.value)}
                rows="3"
                className="w-full p-3 rounded-md bg-gray-900/70 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-sky-400"
                placeholder="e.g., It would be great if you offered..."
              />
            </div>
            
            {error && <p className="text-sm text-center text-red-400">{error}</p>}

            {/* --- BOTONES DE ACCIÓN --- */}
            <div className="flex flex-col sm:flex-row items-center gap-4 pt-4">
              <button
                type="submit"
                disabled={isLoading || !email}
                className="w-full text-lg py-3 px-4 rounded-xl bg-red-600 font-semibold hover:bg-red-700 transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isLoading ? <ArrowPathIcon className="w-6 h-6 animate-spin"/> : 'Confirm Unsubscribe & Send Feedback'}
              </button>
              <Link
                to="/" // O a una página específica de "gracias por quedarte"
                className="w-full sm:w-auto text-center text-gray-300 hover:text-white py-3 px-4 rounded-xl transition-colors"
              >
                Nevermind, I'll Stay
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Unsubscribe;